/**
 * Marketing content for individual feature landing pages.
 * Each feature renders via the shared <FeaturePage> template at /features/<slug>.
 *
 * Adding a new feature page = add an entry below + bump the route in App.js.
 * Keep copy tight: 3-5 sub-features per page, one screenshot/illustration per row.
 */
import {
  IndianRupee, TrendingUp, Banknote, Receipt, Wallet,
  Building2, BedDouble, Home as HomeIcon, Camera,
  Users, UserCheck, UserPlus, ShieldCheck, CalendarCheck,
  Megaphone, ClipboardList, Sparkles, MessageSquare,
  Brush, UtensilsCrossed, Package, UsersRound,
  Bell, Zap, FileText, BarChart3,
} from 'lucide-react';

export const featurePages = {
  money: {
    slug: 'money',
    eyebrow: 'Money Module',
    title: 'Collect rent, dues & deposits — all in one place',
    subtitle:
      'Stop chasing payments on WhatsApp. Track every rupee — rent, electricity, food, deposits — with auto-reminders, live P&L, and one-tap reconciliation.',
    heroAccent: 'green',
    ctaLabel: 'Get a demo',
    ctaSource: 'feature-money',
    bullets: [
      {
        icon: Wallet,
        title: 'Auto Rent & Dues Tracking',
        desc: 'Pre-fills monthly rent for every tenant. One tap to record payment via UPI, cash, or bank transfer.',
      },
      {
        icon: Receipt,
        title: 'Expense Logging with Bill Photos',
        desc: 'Capture vendor bills on your phone. Auto-categorise and tie back to the property they belong to.',
      },
      {
        icon: TrendingUp,
        title: 'Live Profit & Loss Dashboard',
        desc: 'Revenue minus expenses for any period. 6-month trend, expense breakdown, comparison vs last period.',
      },
      {
        icon: Banknote,
        title: 'Banks & UPI Routing',
        desc: 'Different bank account or UPI VPA per property. No more reconciling 5 PGs into 1 account at month-end.',
      },
      {
        icon: MessageSquare,
        title: 'WhatsApp Reminders that Actually Work',
        desc: 'Bulk-send rent reminders with one tap. Auto-stamp who replied. Reply rate displayed on Home.',
      },
    ],
    socialProof:
      '"Our collection time dropped from 14 days to 4. The WhatsApp reminders are gold." — Anjali, 3 PGs in Gurugram',
  },

  properties: {
    slug: 'properties',
    eyebrow: 'Properties & Rooms',
    title: 'Manage 5 PGs as easily as 1',
    subtitle:
      'Per-property dashboards, room-level occupancy, photo uploads, and smart status that auto-flips based on tenant movements.',
    heroAccent: 'blue',
    ctaLabel: 'Get a demo',
    ctaSource: 'feature-properties',
    bullets: [
      {
        icon: Building2,
        title: 'Unlimited Properties',
        desc: 'Add as many as you operate. Each has its own occupancy %, monthly collection, expenses, and P&L.',
      },
      {
        icon: BedDouble,
        title: 'Room & Bed-level Tracking',
        desc: 'Single, double, triple, dorm — track every bed. Auto-flips "available → booked → occupied → notice → available".',
      },
      {
        icon: Camera,
        title: 'Photos for Every Room',
        desc: 'Upload from your phone. Tenants see what they\'re booking. You see what condition it was in at check-in.',
      },
      {
        icon: HomeIcon,
        title: 'Public Listings Generator',
        desc: 'Turn any property into a shareable listing page in seconds. Pre-fills photos, rent, amenities, location.',
      },
      {
        icon: BarChart3,
        title: 'Per-Property Reports',
        desc: 'Occupancy %, collection %, expense breakdown — by property, not just total. Spot the loss-maker fast.',
      },
    ],
    socialProof:
      '"Adding photos and one-click listing took my new PG from 0 to 80% occupied in 11 days." — Rahul, Bengaluru',
  },

  tenants: {
    slug: 'tenants',
    eyebrow: 'Tenants & KYC',
    title: 'From check-in to check-out, fully digital',
    subtitle:
      'Onboard a tenant in 3 minutes. KYC, rent agreement, deposit, all stored. Notice period, exits, refunds — handled smoothly.',
    heroAccent: 'orange',
    ctaLabel: 'Get a demo',
    ctaSource: 'feature-tenants',
    bullets: [
      {
        icon: UserPlus,
        title: 'One-Tap Onboarding',
        desc: 'Booking → move-in → token auto-adjusts to rent or deposit. Choose where to credit it; we handle the ledger.',
      },
      {
        icon: ShieldCheck,
        title: 'KYC Capture on Phone',
        desc: 'Snap Aadhaar front + back + selfie. Pending KYC tenants are flagged on every dashboard.',
      },
      {
        icon: CalendarCheck,
        title: 'Notice Period Workflow',
        desc: 'Put on notice → planned check-out date set automatically. Tenant changes mind? One-click cancel notice.',
      },
      {
        icon: FileText,
        title: 'Rent Agreements (e-Sign)',
        desc: 'Auto-fills your template with tenant + property data. Send for e-signature via Leegality — done in 2 minutes.',
      },
      {
        icon: UserCheck,
        title: 'Full Tenant Profile',
        desc: 'Every payment, every reminder, every note — one timeline per tenant. Searchable. Exportable.',
      },
    ],
    socialProof:
      '"My move-in flow used to take 25 minutes of paperwork. Now it\'s 3. Aadhaar captured on phone, agreement e-signed." — Priya, Delhi NCR',
  },

  leads: {
    slug: 'leads',
    eyebrow: 'Leads & Bookings',
    title: 'Turn every enquiry into a paying tenant',
    subtitle:
      'A real CRM funnel for PG owners. Capture leads from WhatsApp, MagicBricks, NoBroker, walk-ins. Convert with token, then move them in — all tracked.',
    heroAccent: 'purple',
    ctaLabel: 'Get a demo',
    ctaSource: 'feature-leads',
    bullets: [
      {
        icon: Megaphone,
        title: 'Kanban Lead Board',
        desc: 'New → Contacted → Visited → Booked → Moved-In. Drag cards across columns. Last-contacted timestamp on every lead.',
      },
      {
        icon: MessageSquare,
        title: 'WhatsApp Lead Auto-Capture',
        desc: 'When a prospect WhatsApps your business number, the lead lands in "New" automatically. Reply from inside the app.',
      },
      {
        icon: CalendarCheck,
        title: 'Token → Booking → Move-In',
        desc: 'Capture token amount at conversion. Choose to adjust it to rent or deposit when tenant moves in.',
      },
      {
        icon: BarChart3,
        title: 'Funnel KPI on Home',
        desc: 'See visitor → booked → moved-in conversion %. Spot drop-offs. Lost-reason tracking included.',
      },
      {
        icon: Users,
        title: 'Reactivate Lost Leads',
        desc: 'Someone went cold 2 months ago? One-click "reactivate". Move back to Visited, follow up.',
      },
    ],
    socialProof:
      '"Conversion went from 22% to 41% after we started using the Kanban + WA auto-capture combo." — Sandeep, 4 PGs in Pune',
  },

  operations: {
    slug: 'operations',
    eyebrow: 'Operations',
    title: 'Run the PG, not the spreadsheet',
    subtitle:
      'Daily ops your staff actually use — complaints, tasks, food schedule, cleaning checklists, visitor log. All in one app.',
    heroAccent: 'amber',
    ctaLabel: 'Get a demo',
    ctaSource: 'feature-operations',
    bullets: [
      {
        icon: Bell,
        title: 'Complaints Tracker',
        desc: 'Tenants raise it, staff resolves it, you see the trail. SLA timer per complaint, category-wise reports.',
      },
      {
        icon: ClipboardList,
        title: 'Tasks for Staff',
        desc: 'Assign cleaner, cook, security duties with due dates. Photo proof on completion. No more "I forgot".',
      },
      {
        icon: UtensilsCrossed,
        title: 'Food / Mess Module',
        desc: 'Weekly menu, per-tenant food charges, bulk billing. Skip-day support for tenants on travel.',
      },
      {
        icon: Brush,
        title: 'Cleaning Schedule',
        desc: 'Per-room cleaning checklist + photo proof. Tenants see when their room was last cleaned.',
      },
      {
        icon: Package,
        title: 'Assets Inventory',
        desc: 'TV, AC, mattress, geyser — track everything in each room. Condition snapshots at every check-in.',
      },
      {
        icon: UsersRound,
        title: 'Visitor Log',
        desc: 'Every visitor recorded with photo, purpose, and tenant they visited. Police-verification-friendly.',
      },
    ],
    socialProof:
      '"Replaced 6 different WhatsApp groups with this. My staff actually USES it because it\'s simple." — Karan, Mumbai',
  },

  whatsapp: {
    slug: 'whatsapp',
    eyebrow: 'WhatsApp Automation',
    title: 'Your most-used app, now automated',
    subtitle:
      'Auto-rent reminders, welcome flows, lead auto-capture, broadcast announcements. Official Meta Cloud API — never your personal number.',
    heroAccent: 'green',
    ctaLabel: 'Get a demo',
    ctaSource: 'feature-whatsapp',
    bullets: [
      {
        icon: Bell,
        title: 'Auto Rent Reminders',
        desc: 'Day -3, day 0, day +3, day +7 reminders. Each gets a friendlier (or firmer) tone. Reply rate tracked.',
      },
      {
        icon: Sparkles,
        title: 'Welcome Flow',
        desc: 'New tenant gets a "founder-style" welcome message with house rules, contact details, and your photo.',
      },
      {
        icon: Zap,
        title: 'Lead Auto-Capture',
        desc: 'Prospect messages your WA business number → lead created → you get notified → reply from inside the app.',
      },
      {
        icon: Megaphone,
        title: 'Broadcast Announcements',
        desc: '"Water tank cleaning tomorrow 10am". Send to everyone in a property, or filter by floor/room/dues status.',
      },
      {
        icon: ShieldCheck,
        title: 'Official Meta-approved',
        desc: 'Templates vetted by Meta. Your personal number stays private. No ban risk.',
      },
    ],
    socialProof:
      '"WhatsApp reply rate went from 38% to 91% once we switched to template-based reminders." — Neha, 6 properties',
  },

  reports: {
    slug: 'reports',
    eyebrow: 'Reports & Analytics',
    title: 'Numbers that tell you what to fix next',
    subtitle:
      'Live occupancy, collection %, aging buckets, expense breakdown, and a full P&L — by property, not just total. Spot the loss-maker before month-end.',
    heroAccent: 'blue',
    ctaLabel: 'Get a demo',
    ctaSource: 'feature-reports',
    bullets: [
      {
        icon: TrendingUp,
        title: 'Live Profit & Loss',
        desc: 'Revenue minus expenses for any period. 6-month trend, category breakdown, comparison vs last period.',
      },
      {
        icon: BarChart3,
        title: 'Occupancy & Collection %',
        desc: 'Per-property and overall. Set a target and watch it on Home every morning.',
      },
      {
        icon: ClipboardList,
        title: 'Aging Report',
        desc: '0-30, 30-60, 60-90, 90+ day dues — segmented by tenant and property. Stop letting dues pile up unnoticed.',
      },
      {
        icon: Receipt,
        title: 'Expense Categories',
        desc: 'Top 5 expense lines, repeat vendors, seasonality view. Find the leak before it costs you a month.',
      },
      {
        icon: FileText,
        title: 'Export to Excel / PDF',
        desc: 'Every report exports cleanly for your CA, investors, or partner. One tap.',
      },
    ],
    socialProof:
      '"I finally see WHICH property is making money and which is bleeding. Closed one in 2 months." — Vikram, 5 PGs',
  },

  reminders: {
    slug: 'reminders',
    eyebrow: 'Smart Reminders',
    title: 'Rent reminders that get replied to',
    subtitle:
      'Day -3, day 0, day +3, day +7 — auto-scheduled per tenant. Each message gets a friendlier (or firmer) tone. Reply rate tracked on Home.',
    heroAccent: 'amber',
    ctaLabel: 'Get a demo',
    ctaSource: 'feature-reminders',
    bullets: [
      {
        icon: Bell,
        title: 'Auto Schedule',
        desc: 'Set it once. Every tenant gets reminders before, on, and after rent date — without you lifting a finger.',
      },
      {
        icon: MessageSquare,
        title: 'Tone Escalation',
        desc: 'Polite first, firm later. Custom templates per stage. Tenants reply because messages feel human, not robotic.',
      },
      {
        icon: Sparkles,
        title: 'Reply Rate Tracking',
        desc: 'See who replied, who ignored, who promised a date. "Trouble tenants" surface automatically.',
      },
      {
        icon: ShieldCheck,
        title: 'Official WhatsApp Templates',
        desc: 'Meta-approved templates. Your business number stays clean. Zero ban risk.',
      },
      {
        icon: Zap,
        title: 'One-Tap Manual Nudge',
        desc: 'Specific tenant lagging? Send a custom WhatsApp directly from the Dues page in 1 tap.',
      },
    ],
    socialProof:
      '"Auto-reminders alone collected ₹4.2L of pending dues in our first month." — Aman, Hyderabad',
  },

  listings: {
    slug: 'listings',
    eyebrow: 'Public Listings',
    title: 'Turn every PG into a shareable listing in 60 seconds',
    subtitle:
      'Auto-generated listing page per property with photos, rent, amenities, location, and a built-in lead capture form. Share the link on Instagram, WhatsApp, MagicBricks.',
    heroAccent: 'purple',
    ctaLabel: 'Get a demo',
    ctaSource: 'feature-listings',
    bullets: [
      {
        icon: HomeIcon,
        title: 'Instant Listing URL',
        desc: 'Every property auto-publishes at /listings/<your-pg>. Pre-fills photos, rooms, prices, amenities.',
      },
      {
        icon: Camera,
        title: 'Photo Gallery',
        desc: 'Drag-drop photos from phone. Reorder, crop, mark "cover". Tenants see what they\'re booking.',
      },
      {
        icon: UserPlus,
        title: 'Built-in Lead Capture',
        desc: 'Every listing has a "Book a Visit" form. Submissions land directly in your CRM Kanban as new leads.',
      },
      {
        icon: Megaphone,
        title: 'Share to WhatsApp / Instagram',
        desc: 'One-tap share button. Pre-formatted message with link, photos, and your contact.',
      },
      {
        icon: BarChart3,
        title: 'Listing Analytics',
        desc: 'See views, form opens, and conversions per listing. Compare which property attracts more interest.',
      },
    ],
    socialProof:
      '"My Instagram bio link now goes to my RentKollect listing. 18 walk-ins in 3 weeks." — Shruti, Noida',
  },

  agreements: {
    slug: 'agreements',
    eyebrow: 'E-Sign Agreements',
    title: 'Rent agreements signed in 2 minutes, not 2 days',
    subtitle:
      'Auto-fill your template with tenant + property data. Send for e-signature via Leegality. Tenant signs on phone. Legally valid PDF lands in their profile.',
    heroAccent: 'orange',
    ctaLabel: 'Get a demo',
    ctaSource: 'feature-agreements',
    bullets: [
      {
        icon: FileText,
        title: 'One-Click Auto-Fill',
        desc: 'Pulls tenant name, KYC, room, rent, deposit, dates — straight from their profile. Zero typing.',
      },
      {
        icon: ShieldCheck,
        title: 'Aadhaar e-Sign (Leegality)',
        desc: 'Legally valid under IT Act. Tenant signs with Aadhaar OTP on phone. PDF stored permanently.',
      },
      {
        icon: CalendarCheck,
        title: 'Auto-Renewal Reminders',
        desc: 'Agreement expiring in 30 days? You and the tenant get a heads-up. No more missed renewals.',
      },
      {
        icon: UserCheck,
        title: 'Custom Templates',
        desc: 'Upload your own. We render the dynamic fields. One template for short-term, one for long-term — your choice.',
      },
      {
        icon: ClipboardList,
        title: 'Audit Trail',
        desc: 'Every signature timestamped, IP-logged. Lawyer-grade audit log for any dispute.',
      },
    ],
    socialProof:
      '"My agreements used to take a week with printouts and stamp paper. Now 2 minutes. Tenants love it." — Manoj, 3 PGs',
  },
};

export const featureList = Object.values(featurePages);

/**
 * Curated MegaMenu groupings — used in the marketing Navbar dropdown
 * and the /features index page. Order matters (left → right).
 */
export const featureGroups = [
  {
    label: 'Money & Finance',
    items: ['money', 'reports'],
  },
  {
    label: 'Operations',
    items: ['properties', 'tenants', 'leads', 'operations'],
  },
  {
    label: 'Growth & Comms',
    items: ['whatsapp', 'reminders', 'listings', 'agreements'],
  },
];
